import { useState, useEffect } from 'react';
import ButtonPrimary from '../components/style-components/ButtonPrimary';
import { useSelector, useDispatch } from 'react-redux';
import { useNavigate } from 'react-router-dom';
import { initiatePayment } from '../features/orders/orderSlice';
import { getOmnivaLocations } from '../features/shipping/shippingSlice';
import OmnivaSelect from '../components/OmnivaSelect';
import { toast } from 'react-toastify';
import { Helmet } from 'react-helmet';
import SmartpostSelect from '../components/SmartpostSelect';
import ShippingSelection from '../components/ShippingSelection';

function Checkout() {
  const CART_KEY = 'cart_v2';
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [discountObject, setDiscountObject] = useState(null);

  const [juridiskaPersona, setJuridiskaPersona] = useState(false);

  const DELIVERY_PRICE_UNDER_10KG = 299;

  // Add language support
  const currentLang = localStorage.getItem('lang') || 'lv';

  const [formData, setFormData] = useState({
    user_name: '',
    user_surname: '',
    shippingAddress: '',
    shippingCity: '',
    shippingPostcode: '',
    user_phone: '',
    user_email: '',
    company_name: '',
    company_code: '',
    company_address: '',
    company_bank: '',
    company_bank_account: '',
    agree_terms: false,
  });

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({
      ...formData,
      [name]: value
    });
  };

  const [cartItems, setCartItems] = useState([]);
  const { cart, discount } = useSelector((state) => state.order);

  const [omnivaSelected, setOmnivaSelected] = useState(true);
  const [selectedShipping, setSelectedShipping] = useState('smartpost');

  const [selectedPickup, setSelectedPickup] = useState(null);
  const [selectedSmartpost, setSelectedSmartpost] = useState(null);

  const [selectedShippingMethod, setSelectedShippingMethod] = useState('smartpost');
  const [selectedPickupObject, setSelectedPickupObject] = useState(null);
  const [selectedShippingZip, setSelectedShippingZip] = useState('')

  useEffect(() => {
    dispatch(getOmnivaLocations());
  }, [dispatch]);

  const handleCheckboxClick = (e) => {
    const { name, checked } = e.target;
    setFormData((prevFormData) => ({
      ...prevFormData,
      [name]: checked,
    }));
  };
  

  useEffect(() => {
    const storedCartItems = JSON.parse(localStorage.getItem(CART_KEY));
    if (storedCartItems) {
      const parsedItems = storedCartItems.map(item => JSON.parse(item));
      const groupedItems = groupCartItems(parsedItems);
      setCartItems(groupedItems);
    }
    if (!storedCartItems) {
      navigate('/');
      toast.error(currentLang === 'en' ? 'Something went wrong.' : 'Kaut kas nogāja greizi.');
    }
  }, [cart, navigate, currentLang]);

  useEffect(() => {
    const storedDiscount = JSON.parse(localStorage.getItem('discount'));
    if (storedDiscount) {
      setDiscountObject(storedDiscount);
    }
  }, [discount]);

  const groupCartItems = (items) => {
    const itemMap = {};

    items.forEach(item => {
      const key = `${item._id}-${item.variant._id}`;
      if (itemMap[key]) {
        itemMap[key].qty += 1;
      } else {
        itemMap[key] = { ...item, qty: 1 };
      }
    });

    return Object.values(itemMap);
  };

  const getDeliveryPrice = () => {
    return (DELIVERY_PRICE_UNDER_10KG / 100).toFixed(2);
  };

  const getFreeDelivery = () => {
    const price = cartItems.reduce((acc, item) => acc + item.qty * (item.variant.discount_price ? item.variant.discount_price : item.variant.price), 0) >= 4000;
    const coupon = discountObject ? discountObject.delivery? true : false : false;
    return price || coupon;
  };

  const validatePhone = (phone) => {
    const phoneRegex = /^(?:\+371|371)?\s?\d{8}$/;
    return phoneRegex.test(phone);
  };

  const validateForm = () => {
    let isValid = true;

    if (formData.user_name === '') {
      toast.error(currentLang === 'en' ? 'Please enter your first name' : 'Lūdzu ievadiet vārdu');
      isValid = false;
    } else if (formData.user_surname === '') {
      toast.error(currentLang === 'en' ? 'Please enter your last name' : 'Lūdzu ievadiet uzvārdu');
      isValid = false;
    } else if (formData.user_phone === '') {
      toast.error(currentLang === 'en' ? 'Please enter phone number' : 'Lūdzu ievadiet telefona numuru');
      isValid = false;
    } else if (formData.user_email === '') {
      toast.error(currentLang === 'en' ? 'Please enter email address' : 'Lūdzu ievadiet epasta adresi');
      isValid = false;
    } else if ((!selectedShippingZip || isNaN(Number(selectedShippingZip))) && !hasOnlyDigitalProducts()) {
      toast.error(currentLang === 'en' ? 'Please select a delivery method from the list' : 'Lūdzu izvēlieties piegādes metodi no saraksta');
      isValid = false;
    } else if (!validatePhone(formData.user_phone)) {
      toast.error(currentLang === 'en' ? 'Incorrectly entered phone number (example: +371 12345678)' : 'Nepareizi ievadīts telefona numurs (piemērs: +371 12345678)');
      isValid = false;
    } else if(juridiskaPersona && formData.company_name === '') {
      toast.error(currentLang === 'en' ? 'Please enter company name' : 'Lūdzu ievadiet uzņēmuma nosaukumu');
      isValid = false;
    } else if(juridiskaPersona && formData.company_code === '') {
      toast.error(currentLang === 'en' ? 'Please enter company registration number' : 'Lūdzu ievadiet uzņēmuma reģ. numuru');
      isValid = false;
    } else if(juridiskaPersona && formData.company_address === '') {
      toast.error(currentLang === 'en' ? 'Please enter company address' : 'Lūdzu ievadiet uzņēmuma adresi');
      isValid = false;
    } else if(juridiskaPersona && formData.company_bank === '') {
      toast.error(currentLang === 'en' ? 'Please enter company bank name' : 'Lūdzu ievadiet uzņēmuma bankas nosaukumu');
      isValid = false;
    } else if(juridiskaPersona && formData.company_bank_account === '') {
      toast.error(currentLang === 'en' ? 'Please enter company bank account number' : 'Lūdzu ievadiet uzņēmuma bankas konta numuru');
      isValid = false;
    } else if (!formData.agree_terms) {
      toast.error(currentLang === 'en' ? 'Please confirm that you agree to the terms of use' : 'Lūdzu apstipriniet, ka piekrītat lietošanas noteikumiem');
      isValid = false;
    }
    
    return isValid;
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!validateForm()) {
      return;
    }

    const order_object = {
      ...formData,
      pickup: selectedPickup,
      orderItems: cartItems,
      coupon: discountObject ? discountObject._id : null,
      shipping_method: selectedShippingMethod,
      pickup_object: selectedPickupObject,
    };

    if(hasOnlyDigitalProducts()){
      order_object.digital = true;
      order_object.shipping_method = 'digital';
    } else {
      order_object.digital = false;
    }
    
    dispatch(initiatePayment(order_object));
  };

  function calculateTotal(cartItems, discountObject, omnivaSelected, getDeliveryPrice, getFreeDelivery) {
    let itemTotal = cartItems.reduce((acc, item) => {
      const price = item.variant.discount_price ? item.variant.discount_price : item.variant.price;
      return acc + item.qty * price;
    }, 0);
  
    let deliveryTotal = omnivaSelected && !getFreeDelivery() && !hasOnlyDigitalProducts() ? DELIVERY_PRICE_UNDER_10KG : 0;
  
    let discountTotal = 0;
    if (discountObject) {
      if (discountObject.discountAmount) {
        discountTotal = discountObject.discountAmount;
      } else if (discountObject.discountPercentage) {
        discountTotal = itemTotal * (discountObject.discountPercentage / 100);
      }
    }
  
    let finalTotal = Math.max(0, itemTotal + deliveryTotal - discountTotal);
  
    return (finalTotal / 100).toFixed(2);
  }

  const calculateProductPrice = (cartItems) => {
    const total = cartItems.reduce((acc, item) => acc + item.qty * (item.variant.discount_price ? item.variant.discount_price : item.variant.price), 0);
    return (total / 100).toFixed(2) + '€';
  }

  const getProductPrice = (item) => {
    const price = item.variant.discount_price ? item.variant.discount_price : item.variant.price;
    return (price * item.qty / 100).toFixed(2) + '€';
  }

  const getDiscountAmount = (discountObject) => {
    return discountObject.discountAmount ? (discountObject.discountAmount / 100).toFixed(2) + '€' : discountObject.discountPercentage + '%';
  }

  const hasOnlyDigitalProducts = () => {
    return cartItems.every(item => item.digital);
  }

  const shouldDisplayDiscount = () => {
    let hasPercentage = false;
    let hasAmount = false;

    if(discountObject){
      if (discountObject.discountPercentage && discountObject.discountPercentage > 0) {
        hasPercentage = true;
      }
      if (discountObject.discountAmount && discountObject.discountAmount > 0) {
        hasAmount = true;
      }
    }
    
    return hasPercentage || hasAmount;
  }

  const getDiscount = () => {
    if (discountObject) {
      if (discountObject.discountPercentage && discountObject.discountPercentage > 0) {
        return `${discountObject.discountPercentage}%`;
      }
      if (discountObject.discountAmount && discountObject.discountAmount > 0) {
        return `${(discountObject.discountAmount / 100).toFixed(2)}€`;
      }
      if (discountObject.delivery) {
        return currentLang === 'en' ? 'Free delivery' : 'Bezmaksas piegāde';
      }
    }
    return 0;
  }
  
  return (
    <div className="checkout-page padding-40-bottom">
      <Helmet>
        <title>{currentLang === 'en' ? 'Nora Spora - Order' : 'Nora Spora - Pasūtījums'}</title>
        <meta name="description" content="Nora Spora piedāvā plašu klāstu sēņu audzēšanas komplektu, tostarp rozā un pelēkās austersēnes, ežu dižadatenes, gļotainās zvīņenes un drīzumā arī citas." />
        <meta name="keywords" content="nora, spora, sporas, noraspora, supersenes, supersēnes, super, sēnes, maple garden, kļavas dārzs, svaigas sēnes, Trubenieki, Eco Fungus, Austersēnes, Sēņu laboratorija Spora, baravikas, gailenes, bekas, micēlijs, micēlija tapiņas, micēlija dībeļi, rozā austersēnes, austeres, pelēkās austersēnes, zilās austersēnes, lions mane, ežu dižadatene, reiši, reishi, shitake, šitake, šitaki, dzeltenās austersēnes, zeltainās austersēnes, zelta austersēnes, dāvana, dāvanas, hobijs, ekoloģisks, bioloģisks, bio, eko, dabīgs, latvijas sēnes" />
      </Helmet>
      <div className="checkout">
        <h2 className='light'>
          {currentLang === 'en' ? 'Order Checkout' : 'Pasūtījuma noformēšana'}
        </h2>
        <div className="clientType flex gap-10 padding-10-top">
          <button 
            className={`selectButton ${!juridiskaPersona ? "selected" : ""}`} 
            onClick={() => setJuridiskaPersona(false)}
          >
            {currentLang === 'en' ? 'Private Person' : 'Privāta persona'}
          </button>
          <button 
            className={`selectButton ${juridiskaPersona ? "selected" : ""}`} 
            onClick={() => setJuridiskaPersona(true)}
          >
            {currentLang === 'en' ? 'Legal Entity' : 'Juridiska persona'}
          </button>
        </div>
        <form onSubmit={handleSubmit} className='checkout-form padding-20-top'>
          <h4>{currentLang === 'en' ? 'Customer Information' : 'Pasūtītāja informācija'}</h4>
          <div className="split-input">
            <input
              type="text"
              name="user_name"
              className='split-input'
              placeholder={currentLang === 'en' ? 'First Name *' : 'Vārds *'}
              value={formData.user_name}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="user_surname"
              placeholder={currentLang === 'en' ? 'Last Name *' : 'Uzvārds *'}
              value={formData.user_surname}
              onChange={handleInputChange}
              required
            />
          </div>
          {juridiskaPersona &&
          <>
            <input
              type="text"
              name="company_name"
              placeholder={currentLang === 'en' ? 'Company Name *' : 'Uzņēmuma nosaukums *'}
              value={formData.company_name}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="company_code"
              placeholder={currentLang === 'en' ? 'Company Registration No. *' : 'Uzņēmuma Reģ. Nr. *'}
              value={formData.company_code}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="company_address"
              placeholder={currentLang === 'en' ? 'Company Address *' : 'Uzņēmuma adrese *'}
              value={formData.company_address}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="company_bank"
              placeholder={currentLang === 'en' ? 'Company Bank Name *' : 'Uzņēmuma bankas nosaukums *'}
              value={formData.company_bank}
              onChange={handleInputChange}
              required
            />
            <input
              type="text"
              name="company_bank_account"
              placeholder={currentLang === 'en' ? 'Company Bank Account Number *' : 'Uzņēmuma bankas konta numurs *'}
              value={formData.company_bank_account}
              onChange={handleInputChange}
              required
            />
          </>
          }

          <h4 className='padding-20-top'>
            {currentLang === 'en' ? 'Contact Information' : 'Kontaktinformācija'}
          </h4>
          <input
            type="tel"
            name="user_phone"
            placeholder={currentLang === 'en' ? 'Phone (registered in Latvia) *' : 'Tālrunis (reģistrēts Latvijā) *'}
            value={formData.user_phone}
            onChange={handleInputChange}
            required
          />
          <input
            type="email"
            name="user_email"
            placeholder={currentLang === 'en' ? 'Email Address *' : 'Epasta adrese *'}
            value={formData.user_email}
            onChange={handleInputChange}
            required
          />
        </form>
      </div>
      <div className="order-summary">
        <h4>{currentLang === 'en' ? 'Your Order:' : 'Jūsu pasūtījums:'}</h4>
        <ul>
          {cartItems.map((item, index) => (
            <li key={index}>
              {item.qty} x {currentLang === 'en' ? (item.title_english || item.title) : item.title}
              {item._id !== "67594415b22277743b4df42e" && `, ${currentLang === 'en' ? (item.category_english || item.category) : item.category}`}
              - {getProductPrice(item)}
            </li>
          ))}
        </ul>
        {!hasOnlyDigitalProducts() &&
          <ShippingSelection formData={formData} setFormData={setFormData} selectedShippingMethod={selectedShippingMethod} setSelectedShippingMethod={setSelectedShippingMethod} setSelectedPickupObject={setSelectedPickupObject} setSelectedShippingZip={setSelectedShippingZip}/>
        }

        <div className="cart-summary checkout" style={{ width: "100%" }}>
          <div className="cart-summary-info">
            <p>{currentLang === 'en' ? 'Subtotal:' : 'Summa:'}</p>
            <p>{calculateProductPrice(cartItems)}</p>
          </div>
          {omnivaSelected && !hasOnlyDigitalProducts() &&
            <div className="cart-summary-info">
              <p>{currentLang === 'en' ? 'Delivery:' : 'Piegāde:'}</p>
              <p>{getFreeDelivery() ? (currentLang === 'en' ? 'Free' : 'Bezmaksas') : `${getDeliveryPrice()}€`}</p>
            </div>
          }
          {shouldDisplayDiscount() &&
            <div className="cart-summary-info">
              <p>{currentLang === 'en' ? 'Discount:' : 'Atlaide:'}</p>
              <p>{getDiscount()}</p>
            </div>
          }
          <div className="cart-summary-info">
            <p>{currentLang === 'en' ? 'Total:' : 'Kopā:'}</p>
            <p>
              {calculateTotal(cartItems, discountObject, omnivaSelected, getDeliveryPrice, getFreeDelivery)}€
            </p>
          </div>
        </div>
        <div className="flex padding-20-top padding-5-bottom">
          <input
            type="checkbox"
            name="agree_terms"
            checked={formData.agree_terms}
            onChange={() => {}}
            onClick={handleCheckboxClick}
          />
          <span className="padding-10-left">
            {currentLang === 'en' ? (
              <>I agree to the <a href="/lietosanas-noteikumi" className='blue-link' target='_blank'>terms of use</a></>
            ) : (
              <>Piekrītu <a href="/lietosanas-noteikumi" className='blue-link' target='_blank'>lietošanas noteikumiem</a></>
            )}
          </span>
        </div>
        <div className="checkout-button" onClick={handleSubmit}>
          <ButtonPrimary text={currentLang === 'en' ? 'Place Order' : 'Veikt pasūtījumu'} />
        </div>
      </div>
    </div>
  );
}

export default Checkout;
